import { Banner, colors } from '@everlywell/leaves';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { sanitize } from 'utils/domHelpers';
import { ContentfulHtmlLongText } from 'utils/types';

import * as S from './styles';
import { HomepageHeroProps } from './types';

const trackBannerClick = () => {
  ewAnalytics.track({
    event: ANALYTICS.EVENTS.CLICKED_BUTTON,
    data: {
      label: ANALYTICS.LABELS.HOMEPAGE_HERO_BANNER,
    },
  });
};

export const HomepageHero = ({
  headline,
  description,
  mainCta,
  backgroundColor,
  topBanner,
  desktopImage,
  mobileImage,
  imageHeight,
  pinImageToBottom,
  pinImageToRight,
  textColor,
}: HomepageHeroProps): JSX.Element => {
  const headlineHTML = headline as ContentfulHtmlLongText;
  const descriptionHTML = description as ContentfulHtmlLongText;

  const handleClick = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.LABELS.HOMEPAGE_HERO_CTA,
      },
    });
  };

  return (
    <>
      {topBanner?.text && topBanner?.link ? (
        <Banner
          text={topBanner.text}
          url={topBanner.link}
          ctaText={topBanner.ctaText}
          onClick={trackBannerClick}
        />
      ) : null}
      <S.Section
        backgroundColor={backgroundColor || colors.teal6}
        data-testid="hero"
      >
        <S.TextContainer>
          <S.HeadlineAndDescription>
            {headlineHTML ? (
              <S.Headline
                style={{ color: textColor || colors.white }}
                dangerouslySetInnerHTML={{
                  __html: sanitize(headlineHTML.childMarkdownRemark.html),
                }}
              />
            ) : null}
            {descriptionHTML ? (
              <S.Description
                style={{ color: textColor || colors.white }}
                dangerouslySetInnerHTML={{
                  __html: sanitize(descriptionHTML.childMarkdownRemark.html),
                }}
              />
            ) : null}
          </S.HeadlineAndDescription>
          {mainCta ? (
            <S.CTAWrapper>
              <S.MainCTA
                href={mainCta.url}
                appearance={mainCta.appearance || 'hero'}
                onClick={handleClick}
              >
                {mainCta.text}
              </S.MainCTA>
            </S.CTAWrapper>
          ) : null}
        </S.TextContainer>
        {desktopImage ? (
          <S.ImageWrapper
            imageHeight={imageHeight}
            pinImageToBottom={pinImageToBottom}
            pinImageToRight={pinImageToRight}
            useMobileImage={!!mobileImage}
          >
            <GatsbyImage
              image={desktopImage.gatsbyImageData}
              loading="eager"
              alt={desktopImage.description || 'hero image'}
            />
          </S.ImageWrapper>
        ) : null}
        {mobileImage ? (
          <S.MobileImageWrapper>
            <GatsbyImage
              image={mobileImage.gatsbyImageData}
              loading="eager"
              alt={mobileImage.description || 'hero image'}
            />
          </S.MobileImageWrapper>
        ) : null}
      </S.Section>
    </>
  );
};

export default HomepageHero;
